/**
 * EditorialSpine.jsx — GRANULLO_PROOF_RAIL_ORGANISM_V1
 *
 * Intelligence Scroll Spine — the structural nervous system.
 * Proof nodes, scroll-fill progress, scarlet editorial accents.
 * Shares animation language + section awareness with LensOrb.
 *
 * Desktop: fixed right-edge proof rail with scroll-fill
 * Mobile: compact bottom progress ribbon
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SCARLET = '#CD2200';
const GOLD = '#F5A623';

/* ═══ PROOF SECTIONS — synchronized with HomePage.js section IDs ═══ */
const SECTIONS = [
  { id: 'spine-hero',                label: 'THEOREM',     brief: 'Money cannot move incorrectly',         proofType: 'hybrid',   jumpNext: { text: 'See impact capital',     action: 'scroll', target: 'spine-capital-coordinates' } },
  { id: 'spine-capital-coordinates', label: 'COORDINATES', brief: 'Need has an address',                   proofType: 'boundary', jumpNext: { text: 'See the paradox',        action: 'scroll', target: 'spine-paradox' } },
  { id: 'spine-paradox',             label: 'PARADOX',     brief: 'Money ≠ misuse',                        proofType: 'boundary', jumpNext: { text: 'See it at scale',         action: 'scroll', target: 'spine-now' } },
  { id: 'spine-now',                 label: 'SCALE',       brief: '26 promise → arrival scenarios',         proofType: 'hybrid',   jumpNext: { text: 'See historical proof',   action: 'scroll', target: 'spine-history' } },
  { id: 'spine-history',             label: 'HISTORY',     brief: 'Four centuries of trigger failure',     proofType: 'trigger',  jumpNext: { text: 'Walk the corridors',     action: 'scroll', target: 'spine-corridors' } },
  { id: 'spine-corridors',           label: 'CORRIDORS',   brief: 'Documentary truth across borders',      proofType: 'trigger',  jumpNext: { text: 'Play a mission',          action: 'scroll', target: 'spine-play' } },
  { id: 'spine-play',                label: 'PLAY',        brief: 'Release logic training',                proofType: 'hybrid',   jumpNext: { text: 'See the theorem',         action: 'scroll', target: 'spine-theorem' } },
  { id: 'spine-theorem',             label: 'ARCHITECTURE',brief: 'Structural release theorem',            proofType: 'hybrid',   jumpNext: { text: 'Discover your world',     action: 'scroll', target: 'spine-discover' } },
  { id: 'spine-discover',            label: 'DISCOVER',    brief: 'Pattern library for any world',          proofType: 'hybrid',   jumpNext: { text: 'Open the proof loop',    action: 'scroll', target: 'spine-proof' } },
  { id: 'spine-proof',               label: 'PROOF LOOP',  brief: 'Twin · Memory · Compounding',            proofType: 'hybrid',   jumpNext: { text: 'See institutional memory',action: 'scroll', target: 'spine-trust' } },
  { id: 'spine-trust',               label: 'MEMORY',      brief: 'Lattice genealogy + reputation',         proofType: 'hybrid',   jumpNext: { text: 'Activate from your URL',  action: 'scroll', target: 'spine-activation' } },
  { id: 'spine-activation',          label: 'ACTIVATION',  brief: 'Discover programs for YOUR world',       proofType: 'hybrid',   jumpNext: { text: 'See the programs',        action: 'scroll', target: 'spine-programs' } },
  { id: 'spine-programs',            label: 'PROGRAMS',    brief: 'Four deployable pilot packs',            proofType: 'hybrid',   jumpNext: { text: 'Enter simulation room',   action: 'navigate', target: '/sandbox' } },
];

/* ═══ GUIDED DEMO PERSONAS ═══ */
const DEMO_PERSONAS = {
  bank:     { label: 'Bank Demo',         nodes: ['spine-hero', 'spine-corridors', 'spine-play', 'spine-proof', 'spine-programs'], brief: 'LC release logic for banking teams' },
  trade:    { label: 'Trade Finance',     nodes: ['spine-hero', 'spine-now', 'spine-corridors', 'spine-play', 'spine-programs'],   brief: 'Documentary truth for LC teams' },
  treasury: { label: 'Treasury',          nodes: ['spine-hero', 'spine-paradox', 'spine-now', 'spine-play', 'spine-programs'],     brief: 'Capital release controls' },
  csr:      { label: 'CSR / Grants',      nodes: ['spine-hero', 'spine-capital-coordinates', 'spine-play', 'spine-proof', 'spine-programs'], brief: 'Beneficiary release logic' },
  payroll:  { label: 'Payroll / Benefits',nodes: ['spine-hero', 'spine-paradox', 'spine-play', 'spine-proof', 'spine-programs'],   brief: 'Disbursement boundary controls' },
  board:    { label: 'Board Member',      nodes: ['spine-hero', 'spine-history', 'spine-corridors', 'spine-proof', 'spine-programs'], brief: 'Governance + release theorem' },
  investor: { label: 'Investor',          nodes: ['spine-hero', 'spine-play', 'spine-proof', 'spine-trust', 'spine-programs'],     brief: 'Conviction proof journey' },
  rail:     { label: 'Rail Partner',      nodes: ['spine-hero', 'spine-corridors', 'spine-now', 'spine-proof', 'spine-programs'],  brief: 'Integration opportunity map' },
};

export default function EditorialSpine() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [scrollPct, setScrollPct] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [demoMode, setDemoMode] = useState(null);
  const [showPersonaMenu, setShowPersonaMenu] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const railRef = useRef(null);

  // URL-based persona routing: ?demo=trade-finance
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoParam = params.get('demo');
    const corridorParam = params.get('corridor');
    const missionParam = params.get('mission');
    
    if (demoParam) {
      const key = demoParam.replace(/-/g, '').toLowerCase();
      const match = Object.keys(DEMO_PERSONAS).find(k => k === key || k === demoParam || DEMO_PERSONAS[k].label.toLowerCase().replace(/[\s\/]/g, '') === key);
      if (match) { setDemoMode(match); localStorage.setItem('granullo_demo_persona', match); }
    } else {
      const saved = localStorage.getItem('granullo_demo_persona');
      if (saved && DEMO_PERSONAS[saved]) { setShowResume(true); }
    }

    // Auto-scroll to corridor or mission if specified
    if (corridorParam) {
      setTimeout(() => {
        const corridorEl = document.getElementById('spine-corridors');
        if (corridorEl) corridorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Broadcast corridor focus for the map
        window.dispatchEvent(new CustomEvent('granullo-corridor-activate', { detail: { corridorId: corridorParam } }));
      }, 1500);
    } else if (missionParam) {
      setTimeout(() => { window.location.href = `/play/mission/${missionParam}`; }, 1000);
    }
  }, []);

  // Persist demo state
  useEffect(() => {
    if (demoMode) {
      localStorage.setItem('granullo_demo_persona', demoMode);
      localStorage.setItem('granullo_demo_node', SECTIONS[activeIdx]?.id || '');
    }
  }, [demoMode, activeIdx]);

  const resumeWalkthrough = () => {
    const saved = localStorage.getItem('granullo_demo_persona');
    const savedNode = localStorage.getItem('granullo_demo_node');
    if (saved && DEMO_PERSONAS[saved]) {
      setDemoMode(saved);
      setShowResume(false);
      if (savedNode) setTimeout(() => scrollToSection(savedNode), 300);
    }
  };

  const clearDemoMemory = () => {
    setDemoMode(null);
    setShowResume(false);
    localStorage.removeItem('granullo_demo_persona');
    localStorage.removeItem('granullo_demo_node');
  };

  const getShareLink = (personaKey, corridorId, missionId) => {
    let url = `${window.location.origin}?demo=${personaKey}`;
    if (corridorId) url += `&corridor=${corridorId}`;
    if (missionId) url += `&mission=${missionId}`;
    return url;
  };

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
    setScrollPct(pct);

    // Find active section by scroll position
    const sectionEls = SECTIONS.map(s => document.getElementById(s.id));
    let active = 0;
    for (let i = sectionEls.length - 1; i >= 0; i--) {
      if (sectionEls[i]) {
        const rect = sectionEls[i].getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.4) {
          active = i;
          break;
        }
      }
    }
    setActiveIdx(active);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Broadcast active section for Lens synchronization
  const activeSections = demoMode ? SECTIONS.filter(s => DEMO_PERSONAS[demoMode]?.nodes.includes(s.id)) : SECTIONS;

  useEffect(() => {
    window.__granullo_proof_rail = {
      activeSection: SECTIONS[activeIdx]?.id,
      activeLabel: SECTIONS[activeIdx]?.label,
      activeBrief: SECTIONS[activeIdx]?.brief,
      activeProofType: SECTIONS[activeIdx]?.proofType,
      jumpNext: SECTIONS[activeIdx]?.jumpNext,
      demoMode: demoMode ? DEMO_PERSONAS[demoMode]?.label : null,
      scrollPct,
      sections: SECTIONS,
    };
    window.dispatchEvent(new CustomEvent('granullo-proof-rail-update', {
      detail: window.__granullo_proof_rail
    }));
  }, [activeIdx, scrollPct]);

  if (isMobile) {
    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
        padding: '8px 16px', background: 'var(--shell-bg)', borderTop: '1px solid var(--shell-border)',
      }} data-testid="proof-rail-mobile">
        {/* Progress bar */}
        <div style={{ height: 2, background: 'var(--shell-border)', borderRadius: 1, overflow: 'hidden', marginBottom: 6 }}>
          <motion.div style={{ height: '100%', background: SCARLET, borderRadius: 1 }}
            animate={{ width: `${scrollPct * 100}%` }} transition={{ duration: 0.1 }} />
        </div>
        {/* Section dots */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {SECTIONS.map((s, i) => (
            <button key={s.id} onClick={() => scrollToSection(s.id)}
              style={{
                width: i === activeIdx ? 20 : 6, height: 6, borderRadius: 3,
                background: i === activeIdx ? SCARLET : i < activeIdx ? 'var(--shell-text-muted)' : 'var(--shell-border)',
                border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0,
              }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={railRef} style={{
      position: 'fixed', right: 24, top: '50%', transform: 'translateY(-50%)',
      zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center',
    }} data-testid="proof-rail">

      {/* Persona selector trigger */}
      <div style={{ position: 'absolute', top: -28 }}>
        <button onClick={() => setShowPersonaMenu(!showPersonaMenu)}
          style={{
            width: 16, height: 16, borderRadius: '50%', border: `1px solid ${demoMode ? SCARLET : 'var(--shell-border)'}`,
            background: demoMode ? `${SCARLET}15` : 'transparent', cursor: 'pointer', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 8, color: demoMode ? SCARLET : 'var(--shell-text-muted)', transition: 'all 0.2s',
          }}
          data-testid="persona-trigger"
          title={demoMode ? `${DEMO_PERSONAS[demoMode]?.label} mode` : 'Guided demo modes'}>
          {demoMode ? '●' : '◎'}
        </button>
        <AnimatePresence>
          {showPersonaMenu && (
            <motion.div initial={{ opacity: 0, x: 8, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: 8, scale: 0.95 }}
              transition={{ duration: 0.12 }}
              style={{
                position: 'absolute', right: 24, top: -4, whiteSpace: 'nowrap',
                background: 'var(--shell-bg)', border: '1px solid var(--shell-border)',
                borderRadius: 8, padding: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              }}
              data-testid="persona-menu">
              <p style={{ fontSize: 7, fontFamily: "'SF Mono', monospace", color: SCARLET, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '2px 6px', marginBottom: 4 }}>
                Guided Demo
              </p>
              {demoMode && (
                <>
                  <button onClick={() => { clearDemoMemory(); setShowPersonaMenu(false); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 9, padding: '4px 6px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--shell-text-muted)', borderRadius: 3 }}>
                    ✕ Exit guided mode
                  </button>
                  <button onClick={() => { navigator.clipboard.writeText(getShareLink(demoMode)); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', fontSize: 9, padding: '4px 6px', background: 'transparent', border: 'none', cursor: 'pointer', color: GOLD, borderRadius: 3 }}>
                    Copy demo link
                  </button>
                </>
              )}
              {Object.entries(DEMO_PERSONAS).map(([key, p]) => (
                <button key={key}
                  onClick={() => { setDemoMode(key); setShowPersonaMenu(false); scrollToSection(p.nodes[0]); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', fontSize: 9, padding: '4px 6px',
                    background: demoMode === key ? `${SCARLET}10` : 'transparent',
                    border: 'none', cursor: 'pointer', borderRadius: 3,
                    color: demoMode === key ? SCARLET : 'var(--shell-text-secondary)',
                    transition: 'background 0.15s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--shell-surface)'}
                  onMouseOut={e => e.currentTarget.style.background = demoMode === key ? `${SCARLET}10` : 'transparent'}
                  data-testid={`persona-${key}`}>
                  {p.label}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Resume CTA — shows when returning user has saved persona state */}
      <AnimatePresence>
        {showResume && !demoMode && (
          <motion.div initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
            transition={{ delay: 1, duration: 0.3 }}
            style={{
              position: 'absolute', top: -60, right: 24, whiteSpace: 'nowrap',
              background: 'var(--shell-bg)', border: `1px solid ${SCARLET}20`,
              borderRadius: 8, padding: '6px 10px', boxShadow: `0 4px 16px rgba(0,0,0,0.08), 0 0 8px ${SCARLET}08`,
            }}
            data-testid="resume-cta">
            <p style={{ fontSize: 8, fontFamily: "'SF Mono', monospace", color: SCARLET, marginBottom: 3 }}>
              Resume walkthrough
            </p>
            <p style={{ fontSize: 9, color: 'var(--shell-text-secondary)', marginBottom: 4 }}>
              {DEMO_PERSONAS[localStorage.getItem('granullo_demo_persona')]?.label || 'Previous'} — {localStorage.getItem('granullo_demo_node')?.replace('spine-', '') || 'start'}
            </p>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={resumeWalkthrough}
                style={{ fontSize: 8, fontFamily: "'SF Mono', monospace", padding: '2px 8px', background: SCARLET, color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                Resume
              </button>
              <button onClick={clearDemoMemory}
                style={{ fontSize: 8, fontFamily: "'SF Mono', monospace", padding: '2px 8px', background: 'transparent', color: 'var(--shell-text-muted)', border: '1px solid var(--shell-border)', borderRadius: 3, cursor: 'pointer' }}>
                Dismiss
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Vertical proof line */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, width: 1,
        background: 'var(--shell-border)', borderRadius: 1,
      }}>
        {/* Scroll fill */}
        <motion.div style={{
          position: 'absolute', top: 0, width: '100%', borderRadius: 1,
          background: `linear-gradient(180deg, ${SCARLET}, ${GOLD})`,
        }} animate={{ height: `${scrollPct * 100}%` }} transition={{ duration: 0.15, ease: 'easeOut' }} />
      </div>

      {/* Proof nodes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
        {SECTIONS.map((s, i) => {
          const isActive = i === activeIdx;
          const isPast = i < activeIdx;
          const isHovered = hoveredIdx === i;
          const isGuidedNode = demoMode ? DEMO_PERSONAS[demoMode]?.nodes.includes(s.id) : true;
          const isDimmed = demoMode && !isGuidedNode;
          const showLabel = isActive || isHovered;

          return (
            <div key={s.id} style={{ position: 'relative', height: `${88 / SECTIONS.length}vh`, maxHeight: 40, display: 'flex', alignItems: 'center' }}>
              {/* Persistent label — active row stays visible; hover reveals brief */}
              <AnimatePresence mode="wait">
                {showLabel && (
                  <motion.div
                    key={isHovered ? 'hover' : 'active'}
                    initial={{ opacity: 0, x: 4 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 4 }}
                    transition={{ duration: 0.18 }}
                    style={{
                      position: 'absolute', right: 22, whiteSpace: 'nowrap',
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                      pointerEvents: 'none',
                    }}
                  >
                    <span style={{
                      fontSize: 9, fontFamily: "'SF Mono', ui-monospace, monospace", letterSpacing: '0.14em',
                      color: isActive ? SCARLET : 'var(--shell-text-secondary)',
                      fontWeight: 600,
                    }}>{s.label}</span>
                    {isHovered && (
                      <span style={{
                        fontSize: 10.5, fontFamily: "'Caveat', cursive",
                        color: 'var(--shell-text-muted)', marginTop: 1,
                      }}>{s.brief}</span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Node */}
              <button onClick={() => scrollToSection(s.id)}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                aria-label={`Jump to ${s.label} — ${s.brief}`}
                style={{
                  width: isActive ? 10 : isDimmed ? 4 : 6,
                  height: isActive ? 10 : isDimmed ? 4 : 6,
                  borderRadius: '50%',
                  border: `1.5px solid ${isActive ? SCARLET : isPast ? 'var(--shell-text-muted)' : 'var(--shell-border)'}`,
                  background: isPast ? 'var(--shell-text-muted)' : isActive ? SCARLET : 'transparent',
                  cursor: 'pointer', padding: 0, position: 'relative',
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? `0 0 8px ${SCARLET}40, 0 0 16px ${SCARLET}20` : 'none',
                  opacity: isDimmed ? 0.2 : 1,
                }}
                data-testid={`proof-node-${s.id}`}
              >
                {/* Active pulse ring */}
                {isActive && (
                  <motion.div
                    animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                      position: 'absolute', inset: -3, borderRadius: '50%',
                      border: `1px solid ${SCARLET}`,
                    }}
                  />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
